/**
=========================================================
* Tesla Subsea LMS - Header Component
=========================================================
*/

// React imports
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

// Material-UI Icons
import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

// MSAL React
import { useMsal } from "@azure/msal-react";

function Header({ title }) {
  const { instance, accounts } = useMsal(); // Access MSAL instance and account information
  const navigate = useNavigate(); // Router navigation
  const [anchorEl, setAnchorEl] = useState(null); // State for menu anchor
  const [roleSelectionOpen, setRoleSelectionOpen] = useState(false); // State for role selection modal

  const user = accounts[0]?.idTokenClaims || {}; // Extract user claims
  const userRoles = user?.roles || []; // Get roles
  const selectedRole = localStorage.getItem("selectedRole") || ""; // Get stored role

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget); // Open menu
  const handleMenuClose = () => setAnchorEl(null); // Close menu

  const handleSignOut = () => {
    localStorage.removeItem("selectedRole"); // Remove selected role
    instance.logoutRedirect().catch((error) => console.error("Logout failed:", error));
  };

  // Handle role change
  const handleRoleChange = (role) => {
    localStorage.setItem("selectedRole", role); // Store new role
    setRoleSelectionOpen(false); // Close modal
    window.location.reload(); // Reload page to apply role change
  };
  useEffect(() => {
    if (!localStorage.getItem("selectedRole") && userRoles.length === 1) {
      localStorage.setItem("selectedRole", userRoles[0]);
    }
  }, [userRoles]);
  return (
    <AppBar position="fixed" sx={{ backgroundColor: "" }}>
      <Toolbar>
        <MDBox flexGrow={1}>
          {/* Render title only if it exists */}
          {title && (
            <MDTypography variant="h6" color="black">
              {title}
            </MDTypography>
          )}
        </MDBox>

        {/* Profile Icon */}
        <IconButton
          size="large"
          edge="end"
          aria-label="account of current user"
          aria-controls="profile-menu"
          aria-haspopup="true"
          onClick={handleMenuOpen}
          color="##D3D3D3"
        >
          <AccountCircle />
        </IconButton>

        {/* Profile Dropdown Menu */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem>
            <strong>Name:</strong> {user?.name || "Unknown"}
          </MenuItem>
          <MenuItem>
            <strong>Project:</strong> {selectedRole || "No role selected"}
          </MenuItem>
          <MenuItem>
            <strong>User ID:</strong> {user?.oid || "N/A"}
          </MenuItem>
          {/* Change Role Button */}
          {userRoles.length > 1 && (
            <MenuItem onClick={() => setRoleSelectionOpen(true)}>
              <SwapHorizIcon fontSize="small" sx={{ marginRight: 1 }} />
              Switch Project
            </MenuItem>
          )}
          {/* Sign Out Button */}
          <MenuItem onClick={handleSignOut}>
            <LogoutIcon fontSize="small" sx={{ marginRight: 1 }} />
            Sign Out
          </MenuItem>
        </Menu>
      </Toolbar>

      {/* Role Selection Modal */}
      <Menu
        id="role-selection-menu"
        anchorEl={roleSelectionOpen ? document.body : null}
        open={roleSelectionOpen}
        onClose={() => setRoleSelectionOpen(false)}
      >
        <MenuItem disabled>
          <strong>Select a Role</strong>
        </MenuItem>
        <List>
          {userRoles.map((role, index) => (
            <ListItem key={index}>
              <ListItemButton onClick={() => handleRoleChange(role)}>
                <ListItemText primary={role} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Menu>
    </AppBar>
  );
}

// Default props for the header
Header.defaultProps = {
  title: null,
};

// Typechecking props for the header
Header.propTypes = {
  title: PropTypes.string,
};

export default Header;
